"use client";

const SIGNUP_PENDING_KEY = "42a_signup_pending";

// Event names are sent verbatim to Google Analytics. Edit values with care —
// renaming one breaks any GA reports / funnels keyed on the old name.
export enum AnalyticsEvent {
  SignUpPage = "sign_up_page",
  CreateUser = "create_user",
  OnboardingBrandInfo = "onboarding_brand_info",
  OnboardingCompetitors = "onboarding_competitors",
  OnboardingPrompts = "onboarding_prompts",
  ChoosePlan = "choose_plan",
  Checkout = "checkout",
  OnboardingContact = "onboarding_contact",
  PaymentSuccessNewCustomer = "payment_success_new_customer",
  DashboardNewCustomer = "dashboard_new_customer",
  // New onboarding revamp funnel events (3-screen flow)
  OnboardingInputView = "onboarding_input_view",
  OnboardingBuildStart = "onboarding_build_start",
  OnboardingAnalysisVisible = "onboarding_analysis_visible",
  OnboardingPlansVisible = "onboarding_plans_visible",
  OnboardingPlanChosen = "onboarding_plan_chosen",
}

export function trackEvent(name: AnalyticsEvent, params: Record<string, unknown> = {}): void {
  if (process.env.NODE_ENV !== "production") return;
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({event: name, ...params} as any);
}

export interface PaymentSuccessParams {
  /** LemonSqueezy order id, from the subscription doc (webhook-created). */
  transactionId?: string;
  /** Order value in whole USD (not cents). */
  valueUsd?: number;
  planId?: string;
  planName?: string;
  billingCycle?: "monthly" | "annually";
}

/**
 * Fires both post-payment events for a completed first checkout:
 *  1. `payment_success_new_customer` (key event) with transaction/value params.
 *  2. The standard GA4 ecommerce `purchase` event, so revenue shows up in
 *     GA4's native monetization reports.
 * GTM must map the params / enable "send ecommerce data" for these to reach GA4.
 */
export function trackPaymentSuccess(params: PaymentSuccessParams): void {
  trackEvent(AnalyticsEvent.PaymentSuccessNewCustomer, {
    ...(params.transactionId ? {transaction_id: params.transactionId} : {}),
    ...(params.valueUsd != null ? {value: params.valueUsd, currency: "USD"} : {}),
    ...(params.planName ? {plan: params.planName} : {}),
    ...(params.billingCycle ? {billing_cycle: params.billingCycle} : {}),
  });

  if (process.env.NODE_ENV !== "production") return;
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Clear the previous ecommerce object so GTM doesn't merge stale values
  // (recommended by Google's GA4 ecommerce dataLayer docs).
  window.dataLayer.push({ecommerce: null} as any);
  window.dataLayer.push({
    event: "purchase",
    ecommerce: {
      ...(params.transactionId ? {transaction_id: params.transactionId} : {}),
      value: params.valueUsd ?? 0,
      currency: "USD",
      items: [
        {
          item_id: params.planId ?? "unknown",
          item_name: params.planName ?? "Unknown plan",
          price: params.valueUsd ?? 0,
          quantity: 1,
          ...(params.billingCycle ? {item_variant: params.billingCycle} : {}),
        },
      ],
    },
  } as any);
}

export function markSignupPending(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SIGNUP_PENDING_KEY, "1");
  } catch {
    // sessionStorage may be unavailable (SSR, private mode quirks) — ignore
  }
}

export function consumeSignupPending(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(SIGNUP_PENDING_KEY) === "1") {
      sessionStorage.removeItem(SIGNUP_PENDING_KEY);
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}
